import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { useSelector } from "react-redux";
import { baseurl } from "../BaseUrl/Baseurl";
import useSessionState from "./useSessionState";

export const ITEMS_PER_PAGE = 6;

const STALE_KEYS = [
  "masterselecteditems", "areastate", "areaarea",
  "commonfield", "multiselect", "multiselectoptions",
];

export default function useRegionList(field) {
  const userData = useSelector((s) => s.userDetails);
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(0);
  const [tokenMsg, setTokenMsg] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [errorMsg, setErrorMsg] = useState("");
  const [editing, setEditing] = useState(null); // { id, value }
  const [createOpen, setCreateOpen] = useSessionState(`${field}modelstatus`, false);

  const headers = {
    "Content-Type": "application/json",
    Authorization: "Bearer " + userData.token,
  };

  const fetchData = useCallback(() => {
    axios({
      method: "GET",
      url: `${baseurl}/api/get/list?field=${field}&areDetailsRqd=1&isSortingRequired=1&userId=${userData.userId}`,
      headers,
    })
      .then((res) => setData(res.data.message))
      .catch((err) => {
        const msg = err.response?.data?.message;
        if (msg !== undefined) setTokenMsg(msg);
      })
      .finally(() => setIsLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [field, userData.userId, userData.token]);

  const isFirstRender = useRef(true);
  const prevCreateOpen = useRef(createOpen);

  // refetch on mount and whenever the create modal closes
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
      fetchData();
      return;
    }

    if (prevCreateOpen.current && !createOpen) {
      fetchData();
    }
    prevCreateOpen.current = createOpen;
  }, [fetchData, createOpen]);

  // keep the page valid after deletes
  const pageCount = Math.ceil(data.length / ITEMS_PER_PAGE);
  useEffect(() => {
    if (currentPage > 0 && currentPage >= pageCount) setCurrentPage(pageCount - 1);
  }, [currentPage, pageCount]);

  const onDelete = (id) => {
    setSuccessMsg("");
    setErrorMsg("");
    axios({
      method: "DELETE",
      url: `${baseurl}/api/delete?field=${field}&userId=${userData.userId}`,
      headers,
      data: { id },
    })
      .then((res) => {
        fetchData();
        setSuccessMsg(res?.data?.message);
      })
      .catch((err) => {
        const msg = err.response?.data?.message;
        if (msg !== undefined) {
          setTokenMsg(msg);
          setErrorMsg(msg);
        }
      });
  };

  const closeCreate = () => {
    setCreateOpen(false);
    STALE_KEYS.forEach((k) => sessionStorage.removeItem(k));
  };

  const closeEdit = () => {
    setEditing(null);
    fetchData();
  };

  const start = currentPage * ITEMS_PER_PAGE;

  return {
    data, isLoading, tokenMsg, pageCount,
    currentItems: data.slice(start, start + ITEMS_PER_PAGE),
    setCurrentPage,
    successMsg, setSuccessMsg, errorMsg, setErrorMsg,
    editing, setEditing, closeEdit,
    createOpen, openCreate: () => setCreateOpen(true), closeCreate,
    onDelete,
  };
}
